import { useCallback, useEffect, useRef, useState } from "react";

import { type TooltipPlacement } from "antd/lib/tooltip";

import { cn } from "@btnet/utilities-core";

import { BTPopover } from "../BTPopover/BTPopover";
import "./TextTruncate.less";

export interface ITextTruncateProps {
  showPopover?: boolean;
  alwaysShowPopover?: boolean;
  popoverPlacement?: TooltipPlacement | undefined;
  className?: string;
  style?: React.CSSProperties;
  popoverContent?: React.ReactNode;
  getPopoverContainer?: ((triggerNode: HTMLElement) => HTMLElement) | undefined;
  "data-testid"?: string;
  children: React.ReactNode;
}

export const TextTruncate = ({
  showPopover = true,
  alwaysShowPopover,
  "data-testid": dataTestId,
  ...props
}: ITextTruncateProps) => {
  const [isPopoverVisible, setIsPopoverVisible] = useState(false);
  const divRef = useRef<HTMLDivElement | null>(null);

  const classes = cn("text-overflow-auto", props.className);

  /**
   * Whether the text currently overflows its box.
   *
   * Measured on demand rather than tracked, because reading `scrollWidth` forces
   * the browser to flush layout. This component renders once per truncated cell
   * and once per `BTNumberInput`, so measuring every instance whenever any of them
   * resized produced hundreds of synchronous reflows per frame in grids and line
   * item containers — all to keep a value that is only ever read when a popover is
   * about to open. Measuring here costs one reflow per hover and is always current.
   */
  const isTruncated = useCallback(() => {
    const element = divRef.current;
    if (!element) {
      return false;
    }
    return element.clientWidth < element.scrollWidth;
  }, []);

  const isPopoverEnabled = alwaysShowPopover || showPopover;

  useEffect(() => {
    if (isPopoverVisible && !isPopoverEnabled) {
      setIsPopoverVisible(false);
    }
  }, [isPopoverEnabled, isPopoverVisible]);

  const handlePopoverIsVisibleChange = useCallback(
    (visible: boolean) => {
      // Closing never depends on overflow, so skip the measurement entirely.
      if (alwaysShowPopover || (isPopoverEnabled && !visible)) {
        setIsPopoverVisible(visible);
        return;
      }

      if (isPopoverEnabled && isTruncated()) {
        setIsPopoverVisible(visible);
      }
    },
    [alwaysShowPopover, isPopoverEnabled, isTruncated],
  );

  return (
    <div
      ref={divRef}
      className={classes}
      style={props.style}
      data-testid={dataTestId}
    >
      <BTPopover
        content={props.popoverContent ?? props.children}
        placement={props.popoverPlacement ?? "topLeft"}
        mouseEnterDelay={0.3}
        visible={isPopoverVisible}
        onVisibleChange={handlePopoverIsVisibleChange}
        {...(props.getPopoverContainer !== undefined && {
          getPopupContainer: props.getPopoverContainer,
        })}
        disabled={!isPopoverEnabled}
      >
        {props.children}
      </BTPopover>
    </div>
  );
};
export default TextTruncate;
